import { Suspense, useMemo } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageSeo } from "../../../shared/seo/page-seo";
import { Container } from "../../../shared/ui/container";
import { HubTabRail, HubTitleRow, HubSlide, HubFade } from "../../activities/components/hub-top";
import { HubFiltersInline, HubFloatingFilters } from "../../activities/components/hub-dialogs";
import { StaySearchPill } from "../components/stay-search-pill";
import { StaysSearching } from "../components/stays-searching";
import { Icon } from "@majourneys/icons";
import { SubcategoryBar } from "../../activities/components/subcategory-bar";
import type { ExploreBannerSeo } from "../../activities/components/explore-banner";
import { StaysHubGrid } from "../components/stays-hub-grid";
import { StaysMapToggle } from "../components/stays-map-toggle";
import { STAYS_MAP_AVAILABLE } from "../components/stays-hub-map-lazy";
import { useRememberedMap } from "../lib/use-remembered-map";
import {
  inPriceRange,
  matchesStayFilters,
  readStayFilters,
  stayFilterParams,
  stayPriceBounds,
  type StayFilterSearch,
} from "../lib/stay-filters";
import { nightsBetween, useNightlyPrice } from "../lib/nightly";
import { useCurrency } from "../../../lib/currency";
import { readVibes, stayHasVibes, vibesParam } from "../lib/vibes";
import { parseChildAges } from "../lib/nights";
import { sortStayProperties, type StaysCitySortOption } from "../lib/sort-stay-properties";
import { useStayDestinations, useStaysByType } from "../api/stays-query";
import { useCitiesOptional } from "../../activities/hooks/use-cities";
import { useResultsAnchor } from "../../activities/hooks/use-results-anchor";

/**
 * The sort control, in the order a guest reads it.
 *
 * Cheapest first is the default and stays the default: these are hotels
 * charged online in full, and the first question is what they cost. An
 * unpriced property sorts last rather than arbitrarily. That rule lives in
 * sortStayProperties, which this only chooses between.
 */
const SORT_OPTIONS: { value: StaysCitySortOption; key: string; fallback: string }[] = [
  { value: "price_asc", key: "sort.price_asc", fallback: "Price: Low to High" },
  { value: "rating", key: "sort.rating", fallback: "Star rating" },
  { value: "recommended", key: "sort.recommended", fallback: "Recommended" },
];

const DEFAULT_SORT: StaysCitySortOption = "price_asc";

function readSort(value: unknown): StaysCitySortOption {
  return SORT_OPTIONS.some((o) => o.value === value)
    ? (value as StaysCitySortOption)
    : DEFAULT_SORT;
}

/**
 * One property type, across every city we sell hotels in: /en/stays/riad.
 *
 * This replaced the per-city hotel list. A city page duplicated the stays hub
 * (which already filters by city) and split browsing into two paths that had
 * to be kept in step. Type is the axis a guest actually chooses along, and
 * "riads in Marrakech" is the phrase they search for.
 *
 * It is laid out as a category hub (chips, city photo row, sticky sort bar,
 * card grid) and built from those very components, because to a guest this is
 * the same catalogue they were just browsing and a second design would read as
 * a second website.
 *
 * SUPPLIER PROPERTIES ONLY, deliberately — hotels, riads, resorts and, since
 * 2026-08-18, villas. Camps are still sold pay-on-arrival from the activities
 * catalogue. Mixing the two payment models into one list is the confusion this
 * whole feature has been built to avoid, so the note below states which one
 * the reader is on, above the first property rather than under the last.
 */
export function StaysTypePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as {
    lang?: string;
    category?: string;
    city?: string;
  };
  const lang = params.lang ?? i18n.language ?? "en";
  const isRtl = lang === "ar";

  /**
   * The hub slug IS the property type here: /en/category/riad. `hotel`, `riad`
   * and `resort` are not rows in `categories` (see category.$category.tsx,
   * which forks on exactly that), so nothing else on the page can look this up
   * for us. `city` is read as a fallback for the retired /stays/{type} shape.
   */
  const propertyType = params.category ?? params.city ?? "";

  const search = useSearch({ strict: false }) as {
    checkin?: string;
    checkout?: string;
    adults?: number;
    childAges?: string;
    /** Slug of the city the list is narrowed to. Absent means every city. */
    city?: string;
    sort?: string;
    vibe?: string;
    min_price?: string | number;
    max_price?: string | number;
  } & StayFilterSearch;
  const childAges = useMemo(() => parseChildAges(search.childAges), [search.childAges]);

  /*
   * Narrowed to THIS type, so the "Filter by city" row only offers cities that
   * actually hold one. Unfiltered it listed every city with any stay at all,
   * and on the Villas page five of the six chips led to "0 results".
   */
  const { data: destinationsData } = useStayDestinations(propertyType);
  const destinations = useMemo(
    () =>
      (destinationsData?.destinations ?? []).filter(
        (d): d is typeof d & { slug: string } => !!d.slug && (d.propertyCount ?? 0) > 0,
      ),
    [destinationsData],
  );
  const cityNameBySlug = useMemo(
    () => new Map(destinations.map((d) => [d.slug, d.name])),
    [destinations],
  );

  /*
   * City tiles use the same artwork as every other "Filter by city" row on the
   * site, keyed by slug.
   *
   * The stays list carries a photo of one of its own properties, which meant
   * this row showed hotel interiors where the activities pages show the city
   * illustrations, and the same city looked like two different places
   * depending on which chip you were on. The supplier photo stays as the
   * fallback for a city with no artwork of its own.
   */
  const { data: citiesResponse } = useCitiesOptional();
  const cityImageBySlug = useMemo(() => {
    const rows = (citiesResponse?.data ?? []) as { slug: string; image?: string | null }[];
    return new Map(rows.filter((c) => c.image).map((c) => [c.slug, c.image as string]));
  }, [citiesResponse]);

  const { data, isLoading } = useStaysByType(propertyType, {
    checkin: search.checkin,
    checkout: search.checkout,
    adults: search.adults,
    childAges,
  });

  const activeSort = readSort(search.sort);
  /* The same map, filters and moods as the Stays hub (owner, 2026-09-28):
     a guest who taps "Riads" should not lose the tools they just used. */
  const [mapWanted, setMapWanted] = useRememberedMap();
  const mapOn = STAYS_MAP_AVAILABLE && mapWanted;
  const stayFilters = useMemo(
    () => readStayFilters(search),
    // Keyed on the params it reads, not the whole search object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search.stars, search.amen, search.near, search.meal, search.free_cancel, search.pool, search.breakfast],
  );
  const vibes = readVibes(search.vibe);
  const { currency } = useCurrency();
  const priceBounds = stayPriceBounds(currency);
  const priceMin = search.min_price != null && search.min_price !== "" ? Number(search.min_price) : undefined;
  const priceMax = search.max_price != null && search.max_price !== "" ? Number(search.max_price) : undefined;
  const nightlyOf = useNightlyPrice(nightsBetween(search.checkin, search.checkout));
  /* A new type or city brings the list back under the header. Sort, filters,
     moods and the map are changed from inside the list (often from the pinned
     bar), and the grid keeps the guest's place for those itself. */
  const resultsRef = useResultsAnchor([propertyType, search.city ?? ""].join("|"));

  /**
   * Cheapest first unless the guest says otherwise, with unpriced properties
   * last either way. The city filter is applied here rather than by refetching
   * per city: the list this page already holds IS every city, so narrowing it
   * is a filter, not a second round trip.
   */
  const properties = useMemo(() => {
    const all = data?.properties ?? [];
    const scoped = search.city ? all.filter((p) => p.citySlug === search.city) : all;
    return sortStayProperties(scoped, activeSort);
  }, [data, search.city, activeSort]);

  const datesSelected = !!(search.checkin && search.checkout && search.adults);
  const typeLabel = t(`stays.types.${propertyType}`, { defaultValue: propertyType });

  const bannerSeo: ExploreBannerSeo = {
    h1: t("stays.type.heading", { type: typeLabel }),
    kicker: t("nav.stays"),
    lede: t("stays.type.payment_note"),
    categoryName: typeLabel,
    // Only once the guest has narrowed to a city, matching the explore hubs:
    // the crumb reads Home / Explore / Riads / Fez, not a bare country.
    cityName: search.city ? (cityNameBySlug.get(search.city) ?? null) : null,
    bestTime: null,
  };
  // The hub URL, not the retired /stays/{type} one that now 301s here.
  // Pointing the canonical at a redirect asks Google to index a hop.
  const canonicalPath = `/${lang}/category/${propertyType}`;

  /** Everything except the two params this page owns as controls. */
  const carriedSearch = {
    ...(search.checkin ? { checkin: search.checkin } : {}),
    ...(search.checkout ? { checkout: search.checkout } : {}),
    ...(search.adults != null ? { adults: search.adults } : {}),
    ...(search.childAges ? { childAges: search.childAges } : {}),
  };

  function setSearchParam(patch: Record<string, unknown>) {
    void navigate({
      to: "/$lang/category/$category",
      params: { lang, category: propertyType },
      search: ((prev: Record<string, unknown>) => {
        const next = { ...prev, ...patch };
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined) delete next[key];
        }
        return next;
      }) as never,
      resetScroll: false,
    });
  }

  return (
    <>
      <PageSeo
        title={`${t("stays.type.seo_title", { type: typeLabel })} | MaJourneys`}
        description={t("stays.type.seo_description", { type: typeLabel })}
        canonicalPath={canonicalPath}
        lang={lang}
      />

      {/* The same layout as the category hubs. Stays is the lit tab,
          the type tiles carry Camp beside Hotel, Riad, Resort and Villa, and
          the city chip filters by ?city= because these cities come from the
          supplier, not from /category/<type>/<city> pages. */}
      <main
        dir={isRtl ? "rtl" : undefined}
        className="relative min-h-[calc(100dvh_/_var(--mj-ui-zoom,1)_-_68px)] bg-[linear-gradient(180deg,#ffffff_0%,#f7f7fb_45%,#f2f3f8_100%)] pb-16"
      >
        <Suspense fallback={<div className="h-[78px]" aria-hidden />}>
          <HubTabRail lang={lang} activeSlug={propertyType} />
        </Suspense>

        <Container>
          <HubSlide k="stays">
          {/* Types, search and Filters: stacked on a phone, one row on a desk. */}
          <div className="pt-5 lg:flex lg:items-center lg:gap-6">
          <div className="min-w-0 lg:flex-1">
            <Suspense fallback={null}>
              <SubcategoryBar categorySlug={propertyType} lang={lang} search={carriedSearch} />
            </Suspense>
          </div>

          <HubTitleRow
            lang={lang}
            h1={bannerSeo.h1}
            total={properties.length}
            controls={
              <>
              <StaySearchPill
                cities={destinations.map((d) => ({
                  slug: d.slug,
                  name: d.name,
                  image: cityImageBySlug.get(d.slug) ?? d.heroImage ?? undefined,
                }))}
                value={{
                  city: search.city,
                  checkin: search.checkin,
                  checkout: search.checkout,
                  adults: search.adults ?? 2,
                  childAges,
                }}
                // Same page, LiteAPI pricing params set: the list comes back
                // priced for these dates and this party.
                onSearch={(next) =>
                  setSearchParam({
                    city: next.city,
                    checkin: next.checkin,
                    checkout: next.checkout,
                    adults: next.checkin ? next.adults : undefined,
                    childAges: next.checkin && next.childAges.length ? next.childAges.join(",") : undefined,
                  })
                }
              />
              <HubFiltersInline />
              {STAYS_MAP_AVAILABLE ? <StaysMapToggle on={mapOn} onChange={setMapWanted} variant="inline" /> : null}
              </>
            }
          />
          </div>



          {data?.pricingUnavailable ? (
            <p
              role="status"
              className="mb-4 rounded-2xl border border-[rgba(4,0,51,0.08)] bg-white p-4 font-sans text-[13px] text-[#5a5d77]"
            >
              {t("stays.city.pricing_unavailable_message")}
            </p>
          ) : null}

          <div ref={resultsRef} aria-hidden />

          <HubFade k={isLoading ? "loading" : [search.city ?? "all", search.checkin, search.checkout, search.adults, search.childAges].join("|")}>
          <div className="py-6">
            {isLoading ? (
              <StaysSearching
                type={propertyType}
                cityName={search.city ? cityNameBySlug.get(search.city) : null}
                checkin={search.checkin}
                checkout={search.checkout}
              />
            ) : properties.length === 0 ? (
              <p className="font-sans text-[13.5px] text-[#5a5d77]">
                {t("stays.type.empty", { type: typeLabel })}
              </p>
            ) : (
              <StaysHubGrid
                propertyType={propertyType}
                cityFilter={search.city}
                sort={activeSort}
                lang={lang}
                search={{
                  checkin: search.checkin,
                  checkout: search.checkout,
                  adults: search.adults,
                  childAges: search.childAges,
                }}
                mapOn={mapOn}
                filters={stayFilters}
                vibes={vibes}
                onVibesChange={(next) => setSearchParam({ vibe: vibesParam(next) })}
                priceRange={{ min: priceMin, max: priceMax }}
                onMapChange={STAYS_MAP_AVAILABLE ? setMapWanted : undefined}
                stickyBar={{
                  activeType: propertyType,
                  carriedSearch,
                  place: search.city ? cityNameBySlug.get(search.city) : null,
                }}
              />
            )}
          </div>
          </HubFade>
          </HubSlide>

          <HubFloatingFilters
            bounds={priceBounds}
            value={{
              min: priceMin,
              max: priceMax,
              sort: search.sort && search.sort !== DEFAULT_SORT ? search.sort : "",
              stay: stayFilters,
            }}
            sortOptions={SORT_OPTIONS.map((o) => ({
              value: o.value === DEFAULT_SORT ? "" : o.value,
              label: t(o.key, { defaultValue: o.fallback }),
            }))}
            onApply={(next) =>
              setSearchParam({
                sort: next.sort || undefined,
                min_price: next.min,
                max_price: next.max,
                ...stayFilterParams(next.stay),
              })
            }
            priceLabel={t("filter.price_per_night", { defaultValue: "Price per night" })}
            staySection={{
              withDates: !!(search.checkin && search.checkout),
              count: (staged, price) =>
                properties.filter(
                  (p) =>
                    matchesStayFilters(p, staged) &&
                    inPriceRange(nightlyOf(p), price.min, price.max) &&
                    stayHasVibes(p.amenities, vibes),
                ).length,
            }}
            floatingExtra={
              STAYS_MAP_AVAILABLE ? <StaysMapToggle on={mapOn} onChange={setMapWanted} variant="floating" /> : undefined
            }
            staticCount={properties.length}
          />
        </Container>
      </main>
    </>
  );
}
